import { isTCSKeyToProductMappingEnabled } from '@helpCenter/feature-flags';
import { type Message, ExperienceType } from '@atlassian/conversation-assistant';
import type { UHStreamMessage } from '@atlassian/conversation-assistant/dist/types/services/assistance-service/types';
import { getSlugForHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center/advanced-help-center';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { getTotalHelpDesks } from '../utils/ai-mate/get-total-helpdesks';
import { getTotalRequestForms } from '../utils/ai-mate/get-total-requestforms';
import { getTotalSources } from '../utils/ai-mate/get-total-sources';
import { getAnalyticsWebClient } from '../utils/analytics';
import { sendTrackEventForFailure, sendTrackEventForFetchedResponse } from '../utils/analytics/conversation-analytics';
import { getProductIdentifierAttributes } from '../utils/analytics/get-product-identifier-attributes';
import { fetchJson } from './fetch-json';
import { fetchStream } from './fetch-stream';

export const RATE_LIMITED_STATUS_CODE = 429;
export const SUCCESS_STATUS = 200;
export const ERROR_STATUS = 500;

const commonHeaders = {
    'X-Experience-Id': 'unified-help',
    'X-Product': isTCSKeyToProductMappingEnabled() ? 'jira-servicedesk.ondemand' : 'jsm',
};

const helpcenterSlugHeader = {
    'X-Helpcenter-Slug': getSlugForHelpCenter() ?? '',
};

export const getChannelId = async (): Promise<string | null> => {
    const analyticsClient = getAnalyticsWebClient();
    let channelId: string | null = null;

    try {
        const channelResponse = await fetchJson<{ id: string }>(`/gateway/api/assist/chat/v1/channel`, 'POST', {
            body: JSON.stringify({}),
            headers: commonHeaders,
        });

        channelId = channelResponse.id;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendTrackEvent({
            source: 'uhAiMateRequest',
            action: 'fetched',
            actionSubject: 'channelId',
            actionSubjectId: channelId,
            attributes: {
                ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
            },
        });
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendTrackEvent({
            source: 'uhAiMateRequest',
            action: 'fetch_failed',
            actionSubject: 'channelId',
            attributes: {
                ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
            },
        });
        channelId = null;
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    analyticsClient.sendTrackEvent({
        source: 'portalHomeScreen',
        action: 'initiated',
        actionSubject: 'aiInteraction',
        actionSubjectId: channelId ?? '',
        attributes: {
            aiFeatureName: 'aiAnswer',
            aiInteractionID: channelId,
            aiInteractionType: 'conversationId',
            proactiveAIGenerated: 0,
            userGeneratedAI: 1,
        },
    });

    return channelId;
};

export interface AiMateConversationResponse {
    message: Message;
    [key: string]: unknown;
}

export const converseWithAiMate = async ({ channelId, message }: { channelId: string; message: string }) => {
    const analyticsClient = getAnalyticsWebClient();
    const startTime = performance.now();

    try {
        const response = await fetchJson<AiMateConversationResponse>(
            `/gateway/api/assist/chat/v1/channel/${channelId}/message`,
            'POST',
            {
                body: JSON.stringify({
                    content: message,
                }),
                headers: {
                    ...commonHeaders,
                    ...helpcenterSlugHeader,
                },
            }
        );

        if (!response) {
            return {
                status: ERROR_STATUS,
                data: null,
            };
        }

        const metadata = response.message.message_metadata;
        const finishTime = performance.now();
        const messageLatency = finishTime - startTime;

        const totalSources = getTotalSources(response.message);

        const totalHelpDesks = getTotalHelpDesks(response.message);

        const totalRequestForms = getTotalRequestForms(response.message);

        sendTrackEventForFetchedResponse(
            analyticsClient,
            messageLatency,
            metadata?.confidenceScore,
            metadata?.isFallbackMessage,
            channelId,
            totalSources,
            totalHelpDesks,
            totalRequestForms
        );

        return {
            data: response,
            status: SUCCESS_STATUS,
        };
    } catch (error) {
        const status = (error as FetchError).status;
        sendTrackEventForFailure(analyticsClient, channelId, status === RATE_LIMITED_STATUS_CODE);

        return {
            status,
            data: null,
        };
    }
};

export const sendMessageStream = async (
    content: string,
    channelId: string
): Promise<AsyncGenerator<UHStreamMessage>> => {
    const body = {
        content,
    };
    const options: RequestInit = {
        method: 'POST',
        headers: {
            ...commonHeaders,
            'content-type': 'application/json;charset=UTF-8',
            ...helpcenterSlugHeader,
        },
        body: JSON.stringify(body),
    };

    return fetchStream<UHStreamMessage>(`/gateway/api/assist/chat/v1/channel/${channelId}/message/stream`, options);
};
